/**
 * What a black camera frame is, and what the page may do about it.
 *
 * iOS can leave a live, unmuted camera track with valid dimensions and no
 * picture. Three documented causes share that signature:
 *
 * 1. **Template-cloned `<video>`** (WebKit 320979 / 323213). An element cloned
 *    out of an HTML `<template>` never paints on iOS 27. Ours is parser-created
 *    in the LiveView markup, so this is not our case; it is recorded because it
 *    looks identical from the outside.
 * 2. **The preview stops compositing** (WebKit 310349). After
 *    background/foreground, rotation or heavy app switching the element stops
 *    being composited while the track keeps producing frames. Detaching and
 *    re-attaching `srcObject` forces the layer to be recreated and restores the
 *    picture. This costs nothing and never touches the camera.
 * 3. **A wedged capture session.** The OS-side session is stuck. No page code
 *    can fix it, and each extra `getUserMedia` makes recovery less likely.
 *
 * The page can measure only one thing: the **canvas** view, because
 * `sampleVideoLuma` reads through `drawImage`. It cannot measure the composited
 * element. So the honest rule is an ordered, bounded recovery rather than a
 * confident diagnosis:
 *
 *   one `srcObject` bounce (cheap, fixes 2)  ->  re-measure  ->  stop and tell
 *   the fan what to do (1 and 3 need the OS or a reload).
 *
 * Crucially it does **not** re-request the camera automatically. The 2026-10-26
 * field report is "it keeps trying again and again": that was three
 * `getUserMedia` calls against a wedged session.
 */

/** A frame darker than this has no picture. */
export const BLACK_LUMA = 4

/** Cheap, camera-free recovery attempts before the page gives up. */
export const REPAINT_ATTEMPTS = 1

/**
 * A luma sample, or null when there is none yet. `Number(null)` is 0, which
 * would read as a black frame, so absence must be checked before coercion.
 */
function lumaOf(value) {
  if (value == null) return null
  const luma = Number(value)
  return Number.isFinite(luma) ? luma : null
}

/** Is there a picture? Canvas luma is the only signal the page has. */
export function hasPicture({trackLive, trackMuted, videoWidth, canvasLuma}) {
  if (!trackLive || trackMuted || !(videoWidth > 0)) return false
  const luma = lumaOf(canvasLuma)
  // No sample yet: the track is healthy, give it the benefit of the doubt.
  if (luma == null) return true
  return luma >= BLACK_LUMA
}

/**
 * Should the page bounce `srcObject`? Only when the track is healthy and the
 * picture is missing, only a bounded number of times, and never twice at once.
 */
export function shouldRepaint({trackLive, trackMuted, videoWidth, canvasLuma, attempts, repainting, max = REPAINT_ATTEMPTS}) {
  if (repainting) return false
  if (!trackLive || trackMuted || !(videoWidth > 0)) return false
  const luma = lumaOf(canvasLuma)
  if (luma == null || luma >= BLACK_LUMA) return false
  return Number(attempts) < max
}

/**
 * After the cheap attempts are spent: the picture is still missing and the page
 * cannot fix it without the OS. Stop, and say something the fan can act on.
 */
export function shouldStopRetrying({trackLive, trackMuted, videoWidth, canvasLuma, attempts, max = REPAINT_ATTEMPTS}) {
  if (!trackLive || trackMuted || !(videoWidth > 0)) return false
  const luma = lumaOf(canvasLuma)
  if (luma == null || luma >= BLACK_LUMA) return false
  return Number(attempts) >= max
}

/**
 * The message for a picture the page could not restore. It must be actionable
 * from a phone with no dev tools, and it must not send anyone to clear a cache:
 * a wedged capture session survives that, and telling them so wastes the only
 * field trip they have.
 */
export function recoveryMessage() {
  return "The camera is stuck. Close the Camera app and reload this page — or restart the phone."
}

export default {
  BLACK_LUMA,
  REPAINT_ATTEMPTS,
  hasPicture,
  shouldRepaint,
  shouldStopRetrying,
  recoveryMessage,
}
